<template>
  <div v-if="showModule" class="vacations-module">
    <div class="vacations-content" @wheel.stop>
      <div v-if="loading" class="vacations-state">
        <div class="vacations-surface vacations-surface-loading" aria-hidden="true">
          <div v-for="index in 6" :key="index" class="vacations-row vacations-row-skeleton">
            <Skeleton width="7.5rem" height="0.95rem" borderRadius="999px" />
            <div class="vacations-meta">
              <Skeleton width="8.5rem" height="0.85rem" borderRadius="999px" />
              <Skeleton width="0.9rem" height="0.9rem" borderRadius="999px" />
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="error" class="vacations-state">
        <EmptyState
          :title="t('wkt.client.vacations.error-state.title')"
          :description="t('wkt.client.vacations.error-state.description')"
        />
      </div>

      <div v-else-if="bags.length === 0" class="vacations-state">
        <EmptyState
          :title="t('wkt.client.vacations.empty-state.title')"
          :description="t('wkt.client.vacations.empty-state.description')"
        />
      </div>

      <template v-else>
        <div class="vacations-surface">
          <button
            v-for="bag in bags"
            :key="bag.routeBagId ?? 'paid'"
            type="button"
            class="vacations-row"
            @click="openBag(bag)"
          >
            <span class="vacations-title">{{ bag.title }}</span>

            <div class="vacations-meta">
              <span v-if="!bag.paidAbsence" class="vacations-meta-label">
                {{ t('wkt.client.vacations-available-days') }}:
              </span>
              <span v-if="!bag.paidAbsence" class="vacations-value">
                {{ formatDays(bag.availableDays) }}
              </span>
              <i class="pi pi-angle-right vacations-chevron" aria-hidden="true" />
            </div>
          </button>
        </div>

        <button type="button" class="vacations-calendar-link" @click="openCalendar">
          <i class="pi pi-calendar vacations-calendar-icon" aria-hidden="true" />
          <span>{{ t('wkt.client.vacations.show_calendar') }}</span>
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useI18n } from "vue-i18n";
import type { VacationBagSummaryDTO, VacationsModulePayload } from "@wkt/api/vacationsDTO";
import EmptyState from "@wkt/components/common/EmptyState.vue";

const { t, locale } = useI18n();

const showModule = ref(false);
const loading = ref(true);
const error = ref(false);
const bags = ref<VacationBagSummaryDTO[]>([]);

function isBagSummary(item: unknown): item is VacationBagSummaryDTO {
  return Boolean(item) && typeof item === "object";
}

function isVacationsPayload(payload: unknown): payload is VacationsModulePayload {
  return Boolean(payload) && typeof payload === "object";
}

function formatDays(value?: number | null): string {
  return new Intl.NumberFormat([locale.value, "en"], {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value ?? 0);
}

function applyPayload(payload?: unknown): void {
  if (!isVacationsPayload(payload)) {
    loading.value = true;
    error.value = false;
    bags.value = [];
    return;
  }

  loading.value = Boolean(payload.loading);
  error.value = Boolean(payload.error);
  bags.value = Array.isArray(payload.bags)
    ? payload.bags.filter(isBagSummary).map((bag) => ({
        routeBagId: bag.routeBagId ?? null,
        title: bag.title?.trim() || "---",
        availableDays: typeof bag.availableDays === "number" ? bag.availableDays : 0,
        paidAbsence: Boolean(bag.paidAbsence),
      }))
    : [];
}

function onReveal(payload?: unknown): void {
  showModule.value = true;
  applyPayload(payload);
}

function refreshModule(): void {
  showModule.value = true;
}

function openBag(bag: VacationBagSummaryDTO): void {
  const bagId = bag.routeBagId ?? "null";
  window.location.hash = `#vacationsdetail;bagId=${encodeURIComponent(bagId)}`;
}

function openCalendar(): void {
  window.location.hash = "#hcalendar;employeeCalendar=true";
}

defineExpose({
  onReveal,
  refreshModule,
});
</script>

<style scoped>
.vacations-module {
  min-height: 100%;
}

.vacations-content {
  min-height: 100%;
  padding: 18px 12px 24px;
}

.vacations-state {
  min-height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.vacations-surface {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 14px;
  box-shadow: 0 10px 28px rgba(15, 23, 42, 0.06);
  overflow: hidden;
}

.vacations-surface-loading {
  width: 100%;
}

.vacations-row {
  width: 100%;
  border: 0;
  border-bottom: 1px solid rgba(148, 163, 184, 0.18);
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 14px 14px 16px;
  text-align: left;
  cursor: pointer;
}

.vacations-row:last-child {
  border-bottom: 0;
}

.vacations-row:hover {
  background: rgba(var(--primary-rgb, 24, 94, 169), 0.04);
}

.vacations-row-skeleton {
  cursor: default;
}

.vacations-row-skeleton:hover {
  background: transparent;
}

.vacations-title {
  color: #0f172a;
  font-size: 0.92rem;
  font-weight: 500;
  line-height: 1.35;
}

.vacations-meta-label {
  color: #64748b;
  font-size: 0.82rem;
}

.vacations-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.vacations-value {
  color: #475569;
  font-size: 0.86rem;
  font-weight: 500;
}

.vacations-chevron {
  color: var(--primary-color, #185ea9);
  font-size: 0.9rem;
}

.vacations-calendar-link {
  margin-top: 12px;
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  background: transparent;
  color: var(--primary-color, #185ea9);
  font-size: 0.85rem;
  font-weight: 500;
  text-transform: uppercase;
  cursor: pointer;
}

.vacations-calendar-icon {
  font-size: 1rem;
}

@media (max-width: 767px) {
  .vacations-content {
    padding: 14px 10px 20px;
  }

  .vacations-row {
    padding: 14px 12px;
  }

  .vacations-title {
    font-size: 0.9rem;
  }

  .vacations-meta {
    gap: 6px;
  }

  .vacations-meta-label {
    display: none;
  }
}
</style>
